// The dashboard's "how are we doing, what do I do now" views: collection pace
// against last month, the to-dos grouped by kind, and a feed of what just
// happened. Pure functions over lists already fetched, so they're cheap to test.

import type {
  AdminUnit,
  PublicBooking,
  PublicComplaint,
  PublicEmergencyAlert,
  PublicInvoice,
  PublicResident,
} from '@residenone/shared';
import type { ArrearsRow } from './billing-insights';
import { unitAddress } from './unit-address';

const DAY_MS = 86_400_000;

const monthOf = (d: Date, back = 0) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - back, 1)).toISOString().slice(0, 7);

const daysInMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

// ─── Collection pace ──────────────────────────────────────────────────────

export interface MonthPace {
  month: string;
  billedCents: number;
  /** Share of the month's bills paid by the end of each day, 0..1; index 0 = day 1. */
  byDay: number[];
}

export interface CollectionPace {
  current: MonthPace;
  previous: MonthPace;
  /** Today's day of the month; current.byDay runs up to it. */
  day: number;
  /** Paid share today, and last month by the same day. */
  rateToday: number;
  ratePreviousSameDay: number;
}

const billed = (i: PublicInvoice) => i.status === 'PAID' || i.status === 'OPEN' || i.status === 'OVERDUE';

/** Cumulative paid share per day for the bills issued in `month`, through day `upTo`. */
function paceFor(invoices: PublicInvoice[], month: string, upTo: number): MonthPace {
  const bills = invoices.filter((i) => billed(i) && i.issue_date.startsWith(month));
  const billedCents = bills.reduce((s, i) => s + i.total_cents, 0);
  // Payments made within the month, by day; later catch-up payments don't move this month's curve.
  const paidOnDay = new Array<number>(upTo).fill(0);
  for (const i of bills) {
    if (i.status !== 'PAID' || !i.paid_at || !i.paid_at.startsWith(month)) continue;
    const day = Number(i.paid_at.slice(8, 10));
    if (day >= 1 && day <= upTo) paidOnDay[day - 1]! += i.total_cents;
  }
  let running = 0;
  const byDay = paidOnDay.map((cents) => {
    running += cents;
    return billedCents > 0 ? running / billedCents : 0;
  });
  return { month, billedCents, byDay };
}

export function collectionPace(invoices: PublicInvoice[], today: Date): CollectionPace {
  const currentMonth = monthOf(today);
  const previousMonth = monthOf(today, 1);
  const day = today.getUTCDate();
  const current = paceFor(invoices, currentMonth, day);
  const previous = paceFor(invoices, previousMonth, daysInMonth(previousMonth));
  return {
    current,
    previous,
    day,
    rateToday: current.byDay[day - 1] ?? 0,
    ratePreviousSameDay: previous.byDay[Math.min(day, previous.byDay.length) - 1] ?? 0,
  };
}

// ─── To-dos grouped by kind ───────────────────────────────────────────────

export type ActionGroup =
  | { kind: 'EMERGENCY'; count: number; first: PublicEmergencyAlert }
  | { kind: 'BOOKING'; count: number; items: PublicBooking[] }
  | { kind: 'COMPLAINT'; count: number; oldest: PublicComplaint; oldestDays: number }
  | { kind: 'ARREARS'; count: number; serious: number; owedCents: number };

/**
 * One line per kind of task, most urgent first: active emergencies, bookings
 * waiting for approval (soonest first; they can be approved in place), new
 * complaints (with the oldest), then houses behind on fees.
 */
export function groupActions(input: {
  emergencies: PublicEmergencyAlert[];
  bookings: PublicBooking[];
  complaints: PublicComplaint[];
  arrears: ArrearsRow[];
  today: Date;
}): ActionGroup[] {
  const groups: ActionGroup[] = [];
  const active = input.emergencies.filter((e) => e.status === 'ACTIVE');
  if (active.length) groups.push({ kind: 'EMERGENCY', count: active.length, first: active[0]! });

  const pending = input.bookings
    .filter((b) => b.status === 'PENDING_APPROVAL')
    .sort((a, b) => a.start_at.localeCompare(b.start_at));
  if (pending.length) groups.push({ kind: 'BOOKING', count: pending.length, items: pending });

  const fresh = input.complaints
    .filter((c) => c.status === 'NEW')
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  if (fresh.length) {
    const oldest = fresh[0]!;
    groups.push({
      kind: 'COMPLAINT',
      count: fresh.length,
      oldest,
      oldestDays: Math.max(0, Math.floor((input.today.getTime() - Date.parse(oldest.created_at)) / DAY_MS)),
    });
  }

  if (input.arrears.length) {
    groups.push({
      kind: 'ARREARS',
      count: input.arrears.length,
      serious: input.arrears.filter((a) => a.monthsBehind >= 3).length,
      owedCents: input.arrears.reduce((s, a) => s + a.outstandingCents, 0),
    });
  }
  return groups;
}

// ─── Recent activity ──────────────────────────────────────────────────────

export type ActivityEvent =
  | { kind: 'PAYMENT'; at: string; unit: string; amountCents: number }
  | { kind: 'SIGNUP'; at: string; name: string; unit: string | null }
  | { kind: 'COMPLAINT'; at: string; title: string; unit: string | null; id: string }
  | { kind: 'BOOKING'; at: string; facility: string; startsAt: string };

/** The latest things that happened across payments, sign-ups, complaints and bookings, newest first. */
export function activityFeed(input: {
  invoices: PublicInvoice[];
  units: AdminUnit[];
  residents: PublicResident[];
  complaints: PublicComplaint[];
  bookings: PublicBooking[];
  today: Date;
  days?: number;
  limit?: number;
}): ActivityEvent[] {
  const since = new Date(input.today.getTime() - (input.days ?? 14) * DAY_MS).toISOString();
  const now = input.today.toISOString();
  const recent = (at: string | null | undefined): at is string => !!at && at >= since && at <= now;
  const unitLabel = new Map(input.units.map((u) => [u.id, unitAddress(u.block, u.number)]));
  const events: ActivityEvent[] = [];

  for (const i of input.invoices) {
    if (i.status === 'PAID' && recent(i.paid_at)) {
      events.push({ kind: 'PAYMENT', at: i.paid_at, unit: unitLabel.get(i.unit_id) ?? '—', amountCents: i.total_cents });
    }
  }
  for (const r of input.residents) {
    if (recent(r.created_at)) {
      events.push({
        kind: 'SIGNUP',
        at: r.created_at,
        name: r.full_name,
        unit: r.unit_number ? unitAddress(r.unit_block, r.unit_number) : null,
      });
    }
  }
  for (const c of input.complaints) {
    if (recent(c.created_at)) {
      events.push({
        kind: 'COMPLAINT',
        at: c.created_at,
        title: c.title,
        unit: c.unit_number ? unitAddress(c.unit_block, c.unit_number) : null,
        id: c.id,
      });
    }
  }
  for (const b of input.bookings) {
    if (recent(b.created_at)) {
      events.push({ kind: 'BOOKING', at: b.created_at, facility: b.facility?.name ?? '', startsAt: b.start_at });
    }
  }
  return events.sort((a, b) => b.at.localeCompare(a.at)).slice(0, input.limit ?? 8);
}
